/**
 * @typedef {import('../../helpers/dataHelpers/summarizeRespondentOverlap').RespondentOverlap} RespondentOverlap
 */

/**
 * Says who the growth figure on this indicator is built from.
 *
 * The figure compares the average of the first measure moment with the average
 * of the last one. Those two groups need not hold the same people: someone who
 * was already taking part when the organisation started measuring cannot fill
 * in a baseline, so their first questionnaire is a follow-up. The number stays
 * a valid average either way — but without this line a group difference reads
 * as a decline in the participants themselves.
 *
 * Three outcomes, deliberately worded apart: people in both measurements,
 * nobody in both, and "cannot be established" (answers with no participant
 * attached). The last one must never look like a zero.
 * AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
 *
 * @param {object} props
 * @param {RespondentOverlap|null} props.overlap - Output of summarizeRespondentOverlap; null renders nothing.
 */
const RespondentOverlapNote = ({ overlap }) => {
    if (!overlap) return null;

    const {
        firstLabel,
        lastLabel,
        firstRespondents,
        lastRespondents,
        shared,
        identifiable,
        orphanedAnswers,
        unidentifiedAnswers,
    } = overlap;

    const noneShared = identifiable && shared === 0;

    // Bij een anonieme vragenlijst is het aantal personen 0 terwijl er wél
    // antwoorden liggen. Dat getal is dan geen telling maar een onware
    // bewering: er waren mensen, ze zijn alleen niet te herkennen.
    // AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
    const telling = (personen) => (
        identifiable || personen > 0
            ? `${personen} personen`
            : `${unidentifiedAnswers || 0} antwoorden zonder deelnemer`
    );

    return (
        <div
            className={`respondent-overlap-note${noneShared ? ' respondent-overlap-note--warn' : ''}`}
        >
            <p className="respondent-overlap-note-line">
                <span className="respondent-overlap-note-moment">{firstLabel}</span>
                {` ${telling(firstRespondents)} · `}
                <span className="respondent-overlap-note-moment">{lastLabel}</span>
                {` ${telling(lastRespondents)}`}
            </p>

            {!identifiable && (
                <p className="respondent-overlap-note-line">
                    Of dit dezelfde deelnemers zijn is niet vast te stellen — de antwoorden
                    zijn niet aan een deelnemer gekoppeld.
                </p>
            )}

            {noneShared && (
                <p className="respondent-overlap-note-line">
                    Geen enkele deelnemer komt in beide metingen voor. Het verschil tussen
                    beide metingen is een vergelijking van twee verschillende groepen.
                </p>
            )}

            {identifiable && shared > 0 && (
                <p className="respondent-overlap-note-line">
                    {`${shared} van de ${lastRespondents} personen`} komt in beide metingen voor.
                </p>
            )}

            {/* De tabel erboven telt ingevulde vragenlijsten (FormID), deze regel
                telt mensen (Persona). Zonder dat verschil te benoemen staan er twee
                getallen onder elkaar die elkaar lijken tegen te spreken.
                AI-CONTEXT: See docs/pitfalls.md#ui-independent-badges-contradict-each-other */}
            {identifiable && (
                <p className="respondent-overlap-note-line respondent-overlap-note-line--muted">
                    De tabel hierboven telt ingevulde vragenlijsten; deze regel telt personen.
                </p>
            )}

            {orphanedAnswers > 0 && (
                <p className="respondent-overlap-note-line respondent-overlap-note-line--muted">
                    {`${orphanedAnswers} antwoorden`} horen bij een meetmoment dat niet meer
                    bestaat en tellen niet mee.
                </p>
            )}
        </div>
    );
};

export default RespondentOverlapNote;
